/* eslint-disable max-lines */
import { getGridStringOperators, GridColDef, GridFeatureMode, GridFilterModel, } from '@mui/x-data-grid';
import { useCallback, useEffect, useRef, useState } from 'react';

import { deleteSubscription } from '../../services/subscriptions';
import { ToastService } from '../../services/toast.service';
import { deleteUser, exportUsers } from '../../services/usersManagement';
import DataGridTable from '../Shared/DataGridTable';
import { SectionPermissions, useSectionPermissions } from '../Shared/PermissionAccessGuard/hooks/useSectionPermissions';
import { fetchSectionData } from './DataFetch';
import { DeleteSubscription } from './DeleteSubscription';
import { triggerExport } from './Export';
import useUserManagement from './hooks/useUserManagement';
import { getSectionSetup } from './SectionConfig';
type TableRow = { id: string | number;[key: string]: unknown };
export type CopTableProps = {
  sectionType?: string
  enableSearch?: string
  searchPlaceholder?: string
  caseViews?: string
  enableTable?: string
  tableConfig?: string
  productCategory?: string
  enableButton?: string
  buttonLabel?: string
  buttonUrl?: string
  buttonType?: 'action' | 'dropdown'
  errorMessage?: string
  actions?: string
  enableGaShieldToggle?: boolean | string
  showGaShieldUsersLabel?: string
  selectedUsersLabel?: string
  enableMultiSelectUsers?: boolean | string
  deleteSelectedUsersLabel?: string
  notifySelectedUsersLabel?: string
  noUsersSelectedLabel?: string
  actionLabel?: string
}
// sections gated on Agreements permissions; sections not listed have no gate
const SECTION_PERMISSION_MAP: Partial<Record<string, keyof SectionPermissions>> = {
  agreements: 'canViewAgreementsViewList',
  agreementsBySite: 'canViewAgreementsViewList',
  relatedSites: 'canViewAgreementsViewRelatedSites',
  relatedCases: 'canViewAgreementsViewRelatedCases',
};
const CopTable = ({
  sectionType, enableSearch, searchPlaceholder, caseViews, enableTable, tableConfig,
  productCategory, enableButton, buttonLabel, buttonUrl, buttonType, errorMessage,
  actions,enableGaShieldToggle, showGaShieldUsersLabel, selectedUsersLabel, enableMultiSelectUsers,
  deleteSelectedUsersLabel, notifySelectedUsersLabel,noUsersSelectedLabel, actionLabel
}: CopTableProps) => {
  const parsedTableConfig = tableConfig ? JSON.parse(tableConfig) : [];
  const parsedCaseViewConfig = caseViews ? JSON.parse(caseViews) : [];
  const isSearchEnabled = enableSearch === 'true';
  const isTableEnabled = enableTable === 'true';
  const parsedSubscripionActions = actions ? JSON.parse(actions) : [];
  const [rows, setRows] = useState<TableRow[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [legentItem, setLegentItem] = useState<{ color: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [exportAction, setExportAction] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<(string | number)[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState<string | null>(null);
  const [deleteUserIds, setDeleteUserIds] = useState<(string | number)[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [paginationMode, setPaginationMode] = useState<GridFeatureMode>("server");
  const [showGAShield, setShowGAShield] = useState<boolean>(false);
  const [resetSearchKey, setResetSearchKey] = useState(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUserManagementLoaded = useRef(false);
  const [users, setUsers] = useState<TableRow[]>([]);
  const { permissions, isLoading: permissionsLoading } = useSectionPermissions();
  const requiredPermission = sectionType ? SECTION_PERMISSION_MAP[sectionType] : undefined;
  const isSectionPermitted = requiredPermission ? permissions[requiredPermission] : true;
  const { rows: userMgmtRows, totalCount: userMgmtTotalCount } = useUserManagement({
    users: sectionType === 'userManagement' ? users : [],
    searchTerm: sectionType === 'userManagement' ? searchTerm : '',
    showGAShield: sectionType === 'userManagement' ? showGAShield : false,
  });
  useEffect(() => {
    if (sectionType === 'userManagement' && isUserManagementLoaded.current) {
      setRows(userMgmtRows);
      setTotalCount(userMgmtTotalCount);
    }
  }, [sectionType, userMgmtRows, userMgmtTotalCount]);
  const handleOnReload = () => {
    if (sectionType === 'userManagement') {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      isUserManagementLoaded.current = false;
      setSearchTerm('');
      setResetSearchKey(prev => prev + 1);
      setRefreshKey(prev => prev + 1);
    }
  }
  const onDeleteClick = useCallback((id: string) => {
    setDeleteRowId(id);
    setIsDeleteModalOpen(true);
  }, []);
  const onDeleteUsers = useCallback((ids: (string | number)[]) => {
    setDeleteUserIds(ids);
    setIsDeleteModalOpen(true);
  }, []);
  const performDeleteUsers = async (ids: (string | number)[]) => {
    const results = await Promise.all(ids.map(id => deleteUser(id)));
    const failedCount = results.filter(success => !success).length;
    if (failedCount === 0) {
      ToastService.success(`users removed successfully.`);
      isUserManagementLoaded.current = false;
      setRefreshKey(prev => prev + 1);
    } else {
      ToastService.error(`${failedCount} of ${ids.length} user${ids.length > 1 ? 's' : ''} failed to delete. Please try again.`);
    }
  };
  const camelToLabel = (str: string) =>
    str.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
  const filterOptions: { label: string; value: string }[] = (parsedCaseViewConfig?.viewOptions ?? []).map(
    (key: string) => {
      const value = key.startsWith('myCompany')
        ? key.slice(2).charAt(0).toUpperCase() + key.slice(3)
        : key.charAt(0).toUpperCase() + key.slice(1);
      const label = parsedCaseViewConfig?.viewOptionLabels?.[key] ?? camelToLabel(key);
      return { label, value };
    }
  );
  const [selectedFilter, setSelectedFilter] = useState<string>(filterOptions[0]?.value ?? '');
  const casesTrigger = useRef({ searchTerm, selectedFilter, refreshKey, initialized: false });
  const handleSearchChange = (value: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      if (value.length === 0 || value.length >= 3) {
        setSearchTerm(value);
        setPage(1);
      }
    }, 300);
  };
  const handleFilterChange = (model: GridFilterModel) => {
    if (sectionType === 'cases') return; // cases: column filtering handled client-side
    const filter = model.items[0];
    setSearchTerm(filter?.value ?? '');
    setPage(1);
  };
  const stringOperators = getGridStringOperators().filter(({ value }) =>
    ['contains', 'equals', 'startsWith'].includes(value)
  );
  const buildColumns = (allColumns: GridColDef[]) => {
    const actionsColumn = allColumns.find(col => col.field === 'actions');
    if (parsedTableConfig?.columns?.length) {
      const seen = new Set<string>();
      const filtered = parsedTableConfig.columns.reduce(
        (acc: GridColDef[], configCol: { columnDataKey: string; columnName: string; columnOptions?: string[] }) => {
          if (seen.has(configCol.columnDataKey)) return acc;
          const match = allColumns.find(col => col.field === configCol.columnDataKey);
          if (match) {
            seen.add(configCol.columnDataKey);
            const isFilterable = Array.isArray(configCol.columnOptions) && configCol.columnOptions.includes('filter');
            acc.push({
              ...match,
              headerName: configCol.columnName,
              filterable: isFilterable,
              ...(isFilterable && { filterOperators: stringOperators }),
            });
          }
          return acc;
        },
        []
      );
      return actionsColumn ? [...filtered, actionsColumn] : filtered;
    }
    return allColumns;
  };
  useEffect(() => {
    if (!exportAction) return;
    const runExport = async () => {
      try {
        if (sectionType === 'userManagement') {
          // GA Shield ON: export the filtered set, or the subset the user explicitly selected within it
          const userIds = showGAShield
            ? (selectedUserIds.length > 0 ? selectedUserIds : userMgmtRows.map(r => r.id))
            : selectedUserIds;
          await exportUsers({ userIds });
          ToastService.success('Exported to excel.');
        } else {
          const categoryId = Number.parseInt(new URLSearchParams(window.location.search).get('product-category-id') ?? '0', 10);
          await triggerExport(categoryId, exportAction);
        }
      } finally {
        setExportAction(null);
      }
    };
    runExport();
  }, [exportAction])
  useEffect(() => {
    const { columns, legend } = getSectionSetup({ sectionType, viewPageUrl: parsedTableConfig?.viewPageUrl, productCategory, permissions, viewLabel: parsedTableConfig?.viewLabel, subscriptionActions: sectionType === 'subscriptions' || sectionType === 'userManagement' ? parsedSubscripionActions.filter((a: { buttonType: string }) => a.buttonType !== 'add') : [], onDeleteClick, actionLabel });
    setColumns(buildColumns(columns));
    setLegentItem(legend);
    setRows([]);
    setTotalCount(0);
    const clientPaginationSections = new Set(['userManagement', 'userManagementRoles', 'cases']);
    setPaginationMode(sectionType && clientPaginationSections.has(sectionType) ? "client" : "server");
  }, [sectionType, permissions])
  const SUPPORTED_SECTION_TYPES = new Set(['agreements', 'cases', 'relatedSites', 'relatedCases', 'agreementsBySite', 'assets', 'subscriptions', 'userManagement','userManagementRoles']);
  useEffect(() => {
    if (!sectionType || !SUPPORTED_SECTION_TYPES.has(sectionType)) return;
    if (sectionType === 'userManagement' && isUserManagementLoaded.current) return;
    if (requiredPermission && (permissionsLoading || !isSectionPermitted)) return;
    if (sectionType === 'cases') {
      // cases: client-side pagination/filtering — call the API only on search, dropdown or reload
      const prev = casesTrigger.current;
      const shouldFetch =
        !prev.initialized ||
        searchTerm !== prev.searchTerm ||
        selectedFilter !== prev.selectedFilter ||
        refreshKey !== prev.refreshKey;
      casesTrigger.current = { searchTerm, selectedFilter, refreshKey, initialized: true };
      if (!shouldFetch) return; // pagination change → handled client-side, no API call
    }
    const buildCommonParams = (): { top?: number; skip?: number; filter: string } => {
      // cases fetch the full result set (no top/skip); other sections paginate server-side
      if (sectionType === 'cases') return { filter: searchTerm };
      return { top: pageSize, skip: (page - 1) * pageSize, filter: searchTerm };
    };
    const loadData = async () => {
      setIsLoading(true);
      try {
        const commonParams = buildCommonParams();
        const result = await fetchSectionData(sectionType, commonParams, selectedFilter);
        if (result) {
          if (sectionType === 'userManagementRoles') {
            const rolesArray = result as unknown as TableRow[]
            setRows(rolesArray)
            setTotalCount(rolesArray.length)
          } else {
            const odataResult = result as { value: unknown[]; '@odata.count'?: number }
            setRows(odataResult.value as unknown as TableRow[])
            setTotalCount(odataResult['@odata.count'] ?? 0)
            if (sectionType === 'userManagement') {
              setUsers(odataResult.value as unknown as TableRow[])
              isUserManagementLoaded.current = true;
            }
          }
        }
      } finally {
        setIsLoading(false);
      }
    }
    loadData()
  }, [sectionType, page, pageSize, searchTerm, selectedFilter, refreshKey, requiredPermission, permissionsLoading, isSectionPermitted]);
  if (requiredPermission && !permissionsLoading && !isSectionPermitted) return null;
  return (<>
    <p>{isSearchEnabled && isTableEnabled}</p>
    <DeleteSubscription
      isOpen={isDeleteModalOpen}
      onConfirm={async () => {
        if (deleteUserIds.length > 0) {
          await performDeleteUsers(deleteUserIds);
        } else if (deleteRowId) {
          if (sectionType === 'userManagement') {
            const success = await deleteUser(deleteRowId);
            if (success) {
              ToastService.success('User deleted successfully.');
              isUserManagementLoaded.current = false;
              setRefreshKey(prev => prev + 1);
            } else {
              ToastService.error('Failed to delete user. Please try again.');
            }
          } else {
            const success = await deleteSubscription(deleteRowId);
            if (success) {
              ToastService.success('Subscription deleted successfully.');
              setRefreshKey(prev => prev + 1);
            } else {
              ToastService.error('Failed to delete subscription. Please try again.');
            }
          }
        }
        setIsDeleteModalOpen(false);
        setDeleteRowId(null);
        setDeleteUserIds([]);
      }}
      onDismiss={() => { setIsDeleteModalOpen(false); setDeleteRowId(null); setDeleteUserIds([]); }}
      subscriptionLabels={parsedSubscripionActions.find((a: { buttonType: string }) => a.buttonType === 'delete')}
    />
    <DataGridTable
      gridType={sectionType ?? ''}
      rows={rows} columns={columns}
      page={page} pageSize={pageSize} totalCount={totalCount}
      paginationModeType={paginationMode}
      onPageChange={setPage} onPageSizeChange={setPageSize}
      searchPlaceholder={searchPlaceholder}
      onSearchChange={handleSearchChange}
      itemTypeCountLabel={parsedTableConfig?.itemTypeCountLabel}
      itemsPerPageLabel={parsedTableConfig?.itemsPerPageLabel}
      filterOptions={filterOptions}
      selectedFilter={selectedFilter}
      onFilterChange={setSelectedFilter}
      legends={legentItem}
      loading={isLoading}
      noRowsLabel={errorMessage}
      handleFilterChange={handleFilterChange}
      actionButton={(() => {
        if (!buttonLabel || enableButton !== 'true' && buttonType !== 'dropdown') return undefined;
        if (buttonType === 'dropdown') {
          return {
            type: 'export' as const,
            label: buttonLabel,
            enabled: enableButton === 'true',
            options: sectionType === 'userManagement'
              ? [{ label: 'Excel', value: 'excel', onClick: () => setExportAction('excel') }]
              : [
                { label: 'Export As Excel', value: 'excel', onClick: () => setExportAction('excel') },
                { label: 'Export As PDF', value: 'pdf', onClick: () => setExportAction('pdf') },
              ],
          };
        }
        if (buttonUrl) {
          return { type: 'action' as const, label: buttonLabel, href: buttonUrl, enabled: enableButton === 'true' };
        }
        return undefined;
      })()}
      columnActionLabels={parsedTableConfig?.columnActionLabels}
      gaShieldToggle={{
        label: showGaShieldUsersLabel ?? '',
        enableGaShieldToggle: enableGaShieldToggle === true || enableGaShieldToggle === 'true',
        checked: showGAShield,
        onChange: (val) => setShowGAShield(val),
      }}
      selectionActions={{
        label: selectedUsersLabel,
        enableMultiSelectUsers: enableMultiSelectUsers === true || enableMultiSelectUsers === 'true',
        onDelete: onDeleteUsers,
        onSelectionChange: (ids) => setSelectedUserIds(ids),
        notifySelectedUsersLabel:notifySelectedUsersLabel,
        deleteSelectedUsersLabel:deleteSelectedUsersLabel,
        noUsersSelectedLabel:noUsersSelectedLabel,
      }}
      addActionButton={parsedSubscripionActions.find((a: { buttonType: string }) => a.buttonType === 'add')}
      onReload={handleOnReload}
      isUserManagementLoaded={isUserManagementLoaded.current}
      resetSearchKey={resetSearchKey}
    />
  </>);
}
export default CopTable;